import React from "react";
import { TimelineStep, StepBoundary } from "../../../types/timeline";
import { frameToPercent } from "../../../utils/timelineMath";
import { StepHandle } from "./StepHandle";

interface StepRangeBarProps {
  step: TimelineStep;
  totalFrames: number;
  isSelected: boolean;
  isActive: boolean;
  stepRangeLabel: string;
  startHandleLabel: string;
  endHandleLabel: string;
  handleForLabel: string;
  disabled?: boolean;
  onSelect: (stepId: string) => void;
  onHandlePointerDown: (
    stepId: string,
    boundary: StepBoundary,
    e: React.PointerEvent<HTMLButtonElement>,
  ) => void;
  onHandleFocus?: (boundary: StepBoundary) => void;
}

export const StepRangeBar = React.memo(function StepRangeBar({
  step,
  totalFrames,
  isSelected,
  isActive,
  stepRangeLabel,
  startHandleLabel,
  endHandleLabel,
  handleForLabel,
  disabled = false,
  onSelect,
  onHandlePointerDown,
  onHandleFocus,
}: StepRangeBarProps) {
  const leftPercent = frameToPercent(step.startFrame, totalFrames) * 100;
  const rightPercent = frameToPercent(step.endFrame, totalFrames) * 100;
  const widthPercent = Math.max(0, rightPercent - leftPercent);
  const baseColor = step.color ?? "#38bdf8";

  return (
    <div
      className={`timeline-step-range${isActive ? " timeline-step-active" : ""}`}
      style={{
        position: "absolute",
        left: `${leftPercent}%`,
        width: `${widthPercent}%`,
        top: 9,
        height: 24,
        borderRadius: 8,
        border:
          isSelected || isActive
            ? `1px solid ${baseColor}`
            : "1px solid rgba(255,255,255,0.2)",
        background: `linear-gradient(180deg, ${baseColor}EE 0%, ${baseColor}B5 52%, ${baseColor}94 100%)`,
        boxShadow:
          isSelected || isActive
            ? `0 0 0 1px ${baseColor}40, 0 0 18px ${baseColor}55, inset 0 1px 0 rgba(255,255,255,0.22)`
            : "inset 0 1px 0 rgba(255,255,255,0.12)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        paddingInline: 3,
        cursor: disabled ? "not-allowed" : "pointer",
        overflow: "visible",
      }}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(step.id);
      }}
      role="button"
      aria-label={`${stepRangeLabel} ${step.name}`}
      data-oid="fb50rk7"
    >
      <StepHandle
        boundary="start"
        stepName={step.name}
        startLabel={startHandleLabel}
        endLabel={endHandleLabel}
        handleForLabel={handleForLabel}
        onPointerDown={(boundary, e) =>
          onHandlePointerDown(step.id, boundary, e)
        }
        onFocus={onHandleFocus}
        disabled={disabled}
        data-oid=".nc32m3"
      />

      <div
        style={{
          flex: 1,
          marginInline: 6,
          fontSize: 10,
          color: "rgba(255,255,255,0.92)",
          textShadow: "0 1px 0 rgba(0,0,0,0.35)",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          textAlign: "center",
          userSelect: "none",
        }}
        data-oid="hd-bxkh"
      >
        {step.name}
      </div>
      <StepHandle
        boundary="end"
        stepName={step.name}
        startLabel={startHandleLabel}
        endLabel={endHandleLabel}
        handleForLabel={handleForLabel}
        onPointerDown={(boundary, e) =>
          onHandlePointerDown(step.id, boundary, e)
        }
        onFocus={onHandleFocus}
        disabled={disabled}
        data-oid="a-i.bsm"
      />
    </div>
  );
});
